import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { getFullBalance, convertToUsdc } from '../../services/ExchangeService';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import WalletRow from './WalletRow';
import Toast from '../../components/Toast/Toast';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import SelectFiat, { setDefaultFiat, getDefaultFiat } from '../../components/SelectFiat/SelectFiat';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';

function Wallet() {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const history = useHistory();

    const [balances, setBalances] = useState([]);
    const [fiat, setFiat] = useState(getDefaultFiat());
    const [totalFiat, setTotalFiat] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isFuture, setIsFuture] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });

    // UX: search + sort + favorites (so the user doesn't scroll the whole wallet)
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('fiatEstimate');
    const [sortDir, setSortDir] = useState('desc');
    const FAV_KEY = 'goldhub_wallet_favs';
    const [favs, setFavs] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); } catch (e) { return new Set(); }
    });
    const [favOnly, setFavOnly] = useState(false);

    function toggleFav(symbol) {
        setFavs(prev => {
            const next = new Set(prev);
            if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
            localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
            return next;
        });
    }

    useEffect(() => {
        if (!fiat) return;

        setIsLoading(true);
        getFullBalance(fiat, isFuture)
            .then((info) => {
                const balances = Object.entries(info)
                    .filter(item => typeof info[item[0]] === 'object' && item[0] !== 'fiatEstimate')
                    .map(item => ({
                        symbol: item[0],
                        available: item[1].available,
                        onOrder: item[1].onOrder,
                        fiatEstimate: item[1].fiatEstimate,
                        avg: item[1].avg
                    }));

                setBalances(balances.filter(b => parseFloat(b.available) > 0 || parseFloat(b.onOrder) > 0));
                setTotalFiat(info.fiatEstimate);
                setIsLoading(false);
            })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
                setIsLoading(false);
            })
    }, [fiat, isFuture])

    // One-click convert a spot holding to USDC (cancels resting orders + market-sells).
    function handleConvert(asset) {
        if (!window.confirm(t('positions.convertConfirm'))) return;
        convertToUsdc(asset)
            .then(() => { setNotification({ type: 'success', text: t('positions.converted') }); setTimeout(() => history.go(0), 900); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onOrderSubmit(order) {
        history.go(0);
    }

    function onFiatChange(event) {
        setDefaultFiat(event.target.value);
        setFiat(event.target.value);
    }

    function onSort(field) {
        if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortField(field); setSortDir(field === 'symbol' ? 'asc' : 'desc'); }
    }

    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
    const arrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const visible = useMemo(() => {
        let list = balances.slice();
        if (favOnly) list = list.filter(b => favs.has(b.symbol));
        if (search) {
            const q = search.toUpperCase();
            list = list.filter(b => b.symbol.toUpperCase().includes(q));
        }
        list.sort((a, b) => {
            let r;
            if (sortField === 'symbol') r = a.symbol > b.symbol ? 1 : a.symbol < b.symbol ? -1 : 0;
            else r = num(a[sortField]) - num(b[sortField]);
            return sortDir === 'asc' ? r : -r;
        });
        return list;
    }, [balances, search, sortField, sortDir, favOnly, favs]);

    const thStyle = { cursor: 'pointer', userSelect: 'none' };

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4">{t('wallet.title')}</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        {
                            localStorage.getItem("hasFutures") === "true"
                                ? (
                                    <div className='me-2'>
                                        <button className='btn btn-secondary me-2' onClick={() => setIsFuture(!isFuture)}>
                                            {isFuture ? t('common.futures') : t('common.spot')}
                                        </button>
                                    </div>
                                )
                                : <React.Fragment></React.Fragment>
                        }
                        <div className="me-2">
                            <SelectFiat onChange={onFiatChange} fiat={fiat} />
                        </div>
                        <NewOrderButton />
                    </div>
                </div>
                <div className="card card-body border-0 shadow mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <small className="text-muted text-uppercase">{t('wallet.fiatTotal')}</small>
                            <h2 className="mb-0 fw-bold">
                                {(totalFiat && !String(totalFiat).includes('NaN')) ? totalFiat : `~${fiat} 0.00`}
                            </h2>
                        </div>
                        <div className="text-md-end">
                            <small className="text-muted d-block">{isFuture ? t('common.futures') : t('common.spot')}</small>
                            <span className="badge bg-primary">{visible.length}</span>
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                        <input
                            className="form-control"
                            style={{ maxWidth: 320, minWidth: 160 }}
                            type="text"
                            placeholder={`${t('common.symbol')}…`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button
                            type="button"
                            className={`btn ${favOnly ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setFavOnly(v => !v)}
                            title="Favorites only">
                            ★ {favOnly ? 'Favorites' : 'All'}
                        </button>
                    </div>
                    <table className="table table-hover gh-table-stack-sm">
                        <thead>
                            <tr>
                                <th className="border-gray-200" style={thStyle} onClick={() => onSort('symbol')}>{t('common.symbol')}{arrow('symbol')}</th>
                                <th className="border-gray-200" style={thStyle} onClick={() => onSort('available')}>{t('wallet.available')}{arrow('available')}</th>
                                <th className="border-gray-200" style={thStyle} onClick={() => onSort('onOrder')}>{t('wallet.locked')}{arrow('onOrder')}</th>
                                <th className="border-gray-200" style={thStyle} onClick={() => onSort('fiatEstimate')}>{t('wallet.fiatTotal')}{arrow('fiatEstimate')}</th>
                                <th className="border-gray-200" style={thStyle} onClick={() => onSort('avg')}>{t('wallet.avgPrice')}{arrow('avg')}</th>
                                <th className="border-gray-200">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                isLoading
                                    ? (<tr><td colSpan={6}>{t('common.loading')}</td></tr>)
                                    : visible.length
                                        ? visible.map(item => (<WalletRow key={item.symbol} data={item} isFav={favs.has(item.symbol)} onToggleFav={toggleFav} onConvert={handleConvert} canConvert={!isFuture} />))
                                        : (<tr><td colSpan={6} className="text-muted">{favOnly ? 'No favorites yet — tap ☆ to add.' : 'No results found.'}</td></tr>)
                            }
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={6}>
                                    <span className="text-muted me-2">{t('wallet.fiatTotal')}</span>
                                    <span className="fw-bold">{(totalFiat && !String(totalFiat).includes('NaN')) ? totalFiat : `~${fiat} 0.00`}</span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <Footer />
            </main>
            <NewOrderModal onSubmit={onOrderSubmit} />
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Wallet;
